'use client'

import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { User } from '@/lib/types'
import { LightbulbIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { ChatKitPageWrapper } from '../_components/chatkit-page-wrapper'
import { ChatKitLoginGate } from '../_components/chatkit-login-gate'
import { getChatKitConstants } from './_const'

interface IBusinessNamingClientProps {
  user: User | null
  userApiKey: string
  hasApiKey: boolean
}

/**
 * Client component for Business Naming Agent
 * Handles ChatKit integration and user interactions
 */
export default function BusinessNamingClient({
  user,
  userApiKey,
  hasApiKey,
}: IBusinessNamingClientProps) {
  const t = useTranslations('business-naming-agent')
  const constants = getChatKitConstants(t)

  if (!user) {
    return (
      <ChatKitLoginGate
        icon={LightbulbIcon}
        title="Log in to name your business with AI"
        description="Explore the Business Naming Agent’s prompts and outputs. Sign in to save sessions, check domain availability, and sync names to projects."
        redirectUrl="/chatkit/business-naming-agent"
      />
    )
  }

  if (!hasApiKey) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <LightbulbIcon />
          </EmptyMedia>
          <EmptyTitle>No API Key</EmptyTitle>
          <EmptyDescription>
            Generate an API key inside your workspace settings to use the
            Business Naming Agent.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  return (
    <ChatKitPageWrapper
      user={user}
      userApiKey={userApiKey}
      workflowKey="business_naming"
      constants={constants}
      t={t}
      icon={LightbulbIcon}
    />
  )
}
